import * as types from 'models/actionTypes'
import * as compositionsApi from 'services/compositions'
import { getRenderer } from 'models/refStore/selectors'

import {
    getSelectedComposition,
    getSelectedCompositionData,
    getSelectedCompositionId,
    getSelectedRenderItemId,
    getSelectedCompositionSaveData,
    getIsModified,
} from 'models/compositions/selectors'
import { getIsLoggedIn, getUserId } from 'models/user/selectors'
import { loginNeeded } from 'models/user/actions'
import { getShowDesignSummary } from 'models/ui/selectors'

const requestSaveComposition = (data, trackingInfo) => ({
    type: types.REQUEST_SAVE_COMPOSITION,
    data,
    trackingInfo,
})

const receiveSaveComposition = (composition, isNew = false, originId) => ({
    type: types.RECEIVE_SAVE_COMPOSITION,
    composition,
    isNew,
    compositionId: composition.compositionId,
    originId,
})

const failedSaveComposition = (error) => ({
    type: types.FAILED_SAVE_COMPOSITION,
    error,
})

export const requestCompositions = () => ({
    type: types.REQUEST_COMPOSITIONS_SAGA,
})

export const receiveCompositions = (compositions) => ({
    type: types.RECEIVE_COMPOSITIONS,
    compositions,
})

export const failedReceiveCompositions = () => ({
    type: types.FAILED_RECEIVE_COMPOSITIONS,
})

export const requestCompiledComposition = (id) => ({
    type: types.REQUEST_COMPILED_COMPOSITION_SAGA,
    id,
})

export const receiveCompiledComposition = (composition, scene, furniture) => ({
    type: types.RECEIVE_COMPILED_COMPOSITION,
    composition,
    scene,
    furniture,
})

export const updateHiddenSurfaces = (compositionId, surfaces) => ({
    type: types.UPDATE_HIDDEN_SURFACES,
    compositionId,
    surfaces,
})

export const failedReceiveCompiledComposition = () => ({
    type: types.FAILED_RECEIVE_COMPILED_COMPOSITION,
})

export const setCompositionPreFetchLoading = (isLoading) => ({
    type: types.SET_COMPOSITION_PRE_FETCH_LOADING,
    isLoading,
})

export const startDragRenderItem = (uniqueId) => ({
    type: types.START_DRAG_RENDER_ITEM,
    uniqueId,
})

export const stopDragRenderItem = () => ({
    type: types.STOP_DRAG_RENDER_ITEM,
})

export const startPlaceStackableItem = (settings, trackingInfo) => ({
    type: types.START_PLACE_STACKABLE_ITEM,
    settings,
    trackingInfo,
})

export const startSwapStackableSurface = () => ({
    type: types.START_SWAP_STACKABLE_SURFACE,
})

export const stackableSurfaceClicked = (planeId, x, y) => ({
    type: types.STACKABLE_SURFACE_CLICKED,
    planeId,
    x,
    y,
})

export const swapStackableSurface = (compositionId, itemId, planeId, x, y) => ({
    type: types.SWAP_STACKABLE_SURFACE,
    compositionId,
    itemId,
    planeId,
    x,
    y,
})

export const placeRenderItem = (compositionId, id, x, y, settings, trackingInfo) => ({
    type: types.PLACE_RENDER_ITEM,
    id,
    compositionId,
    x,
    y,
    settings,
    trackingInfo,
})

export const swapStackableToFloor = (compositionId, itemId, x, y) => ({
    type: types.SWAP_STACKABLE_TO_FLOOR,
    compositionId,
    itemId,
    x,
    y,
})

export const clickPlaceRenderItem = (compositionId, id, settings, trackingInfo) => ({
    type: types.CLICK_PLACE_RENDER_ITEM,
    id,
    compositionId,
    settings,
    trackingInfo,
})

export const clickPlaceStackableItem = (planeId, x, y) => ({
    type: types.CLICK_PLACE_STACKABLE_ITEM,
    planeId,
    x,
    y,
})

export const clickPlaceStackableOnFloor = () => ({
    type: types.CLICK_PLACE_STACKABLE_ON_FLOOR,
})

const requestDeleteComposition = (id) => ({
    type: types.REQUEST_DELETE_COMPOSITION,
    id,
})

const receiveDeleteComposition = () => ({
    type: types.RECEIVE_DELETE_COMPOSITION,
})

// Select another composition
export const selectComposition = (compositionId) => ({
    type: types.SELECT_COMPOSITION,
    compositionId,
})

export const openComposition = (compositionId, trackingInfo) => ({
    type: types.OPEN_COMPOSITION,
    compositionId,
    trackingInfo,
})

export const deleteComposition = (id) => (dispatch) => {
    dispatch(requestDeleteComposition(id))

    return compositionsApi.deleteComposition(id).then(() => dispatch(receiveDeleteComposition()))
}

export const saveSelectedComposition =
    (trackingInfo = {}) =>
    (dispatch, getState) => {
        const state = getState()

        const isLoggedIn = getIsLoggedIn(state)
        if (!isLoggedIn) {
            dispatch(loginNeeded())
            return // Return early if not logged in
        }

        const compositionData = getSelectedCompositionData(state)
        const compositionSaveData = getSelectedCompositionSaveData(state)
        const userId = getUserId(state)

        dispatch(requestSaveComposition(compositionSaveData, trackingInfo))

        const isNew = // Create a new composition
            compositionSaveData.compositionId === -1 || // if using the default comp
            compositionData.wid !== userId // or if you don't own the comp
        const isShowingDesignSummary = getShowDesignSummary(state)
        getRenderer().takeRoomScreenshot(
            (baseImageData) => {
                compositionSaveData.baseImageData = baseImageData.thumbDataUrl
                return compositionsApi
                    .saveComposition(compositionSaveData, isNew)
                    .then(({ data }) => {
                        dispatch(receiveSaveComposition(data, isNew, compositionData.id))
                        // Do not open the new composition if we are showing Design Summary
                        // because Save and exit in design summary changes location url.
                        if (isNew && !isShowingDesignSummary) {
                            dispatch(openComposition(data.compositionId))
                        }
                    })
                    .catch(() => dispatch(failedSaveComposition()))
            },
            (error) => {
                dispatch(failedSaveComposition(error))
            },
        )
    }

export const deleteCompositionRenderItem = (compositionId, id, trackingInfo) => ({
    type: types.DELETE_COMPOSITION_RENDER_ITEM,
    compositionId,
    id,
    trackingInfo,
})

export const selectCompositionRenderItem = (renderItemId, furnitureId = null) => ({
    type: types.SELECT_COMPOSITION_RENDER_ITEM,
    renderItemId,
    furnitureId, // furnitureId is needed for displaying swapabble items
})

export const updateCompostitionRenderItem = (compositionId, id, item, trackingInfo) => ({
    type: types.UPDATE_COMPOSITION_RENDER_ITEM,
    compositionId,
    id,
    item, // Item, here, is an object containing the fields that we want to overwrite
    trackingInfo,
})

export const clearSwapAffinity = (compositionId, id) => ({
    type: types.CLEAR_SWAP_AFFINITY,
    compositionId,
    id,
})

export const updateSelectedCompositionRenderItem = (item, trackingInfo) => (dispatch, getState) => {
    const compositionId = getSelectedCompositionId(getState())
    const renderItemId = getSelectedRenderItemId(getState())

    dispatch(updateCompostitionRenderItem(compositionId, renderItemId, item, trackingInfo))
}

export const renameComposition = (id, name) => ({
    type: types.RENAME_COMPOSITION,
    name,
    id,
})

export const renameSelectedComposition =
    (name, trackingInfo = {}) =>
    (dispatch, getState) => {
        const id = getSelectedCompositionId(getState())
        dispatch(renameComposition(id, name))
        dispatch(saveSelectedComposition(trackingInfo))
    }

const setIsSharedComposition = (compositionId) => ({
    type: types.SHARE_COMPOSITION,
    id: compositionId,
})

export const saveCompositionBeforeSharing =
    (trackingInfo = {}) =>
    (dispatch, getState) => {
        const state = getState()
        const composition = getSelectedComposition(state)
        const isModified = getIsModified(state)

        const prevIsShared = composition.isShared
        // Set the isShared field of the composition to true
        dispatch(setIsSharedComposition(composition.id))

        // Save the composition before sharing if it has been modified,
        // so the generated thumbnail will reflect the user's changes.
        // Or, save the composition if the user is sharing this composition
        // for the first time, ie, the previous value isShared field was false.
        if (isModified || !prevIsShared) {
            dispatch(saveSelectedComposition(trackingInfo))
        }
    }

export const renderItemLoaded = (compositionId, id, item) => ({
    type: types.RENDER_ITEM_LOADED,
    compositionId,
    id,
    item,
})

export const wallartItemLoaded = (compositionId, id, item) => ({
    type: types.WALLART_ITEM_LOADED,
    compositionId,
    id,
    item,
})

export const swapRenderItem = (targetRenderItemId, productId, trackingInfo) => ({
    type: types.SWAP_RENDER_ITEM,
    targetRenderItemId,
    productId,
    trackingInfo,
})

export const duplicateRenderItem = (targetRenderItemId, renderType, trackingInfo) => ({
    type: types.DUPLICATE_RENDER_ITEM,
    targetRenderItemId,
    renderType,
    trackingInfo,
})

export const updateCompositionSaveData = (compositionId, saveData, trackingInfo = null) => ({
    type: types.UPDATE_COMPOSITION_SAVE_DATA,
    compositionId,
    saveData,
    trackingInfo,
})

export const removeRenderItems = (compositionId) => ({
    type: types.REMOVE_RENDER_ITEMS,
    compositionId,
})

export const undoRemoveRenderItems = (compositionId) => ({
    type: types.UNDO_REMOVE_RENDER_ITEMS,
    compositionId,
})

export const scheduleDelayedScreenshot = () => ({
    type: types.SCHEDULE_DELAYED_SCREENSHOT,
})

export const delayedScreenshotScsheduled = (timeoutId) => ({
    type: types.DELAYED_SCREENSHOT_SCHEDULED,
    timeoutId,
})

export const delayedScreenshotSent = () => ({
    type: types.DELAYED_SCREENSHOT_SENT,
})

export const firstRenderItemScreenshotDone = () => ({
    type: types.FIRST_RENDER_ITEM_SCREENSHOT_DONE,
})

export const setWallColor = (compositionId, planeIndex, colorString, trackingInfo = {}) => ({
    type: types.SET_WALL_COLOR,
    compositionId,
    planeIndex,
    colorString,
    trackingInfo,
})
